<template>
  <div class="metrics-table" :class="{ 'loading': isLoading }">
    <div class="table-header">
      <h3 v-if="title" class="table-title">{{ title }}</h3>
      
      <div class="table-controls">
        <!-- Search filter -->
        <div class="search-filter">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search..." 
            class="search-input"
          />
        </div>
        
        <!-- Additional filters -->
        <div v-if="filters.length > 0" class="filters-container">
          <div v-for="(filter, index) in filters" :key="index" class="filter">
            <label :for="`filter-${index}`" class="filter-label">{{ filter.label }}:</label>
            <select 
              :id="`filter-${index}`" 
              v-model="activeFilters[filter.key]" 
              class="filter-select"
            >
              <option value="">All</option>
              <option 
                v-for="option in filter.options" 
                :key="option.value" 
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    
    <div class="table-responsive">
      <table class="data-table">
        <thead>
          <tr>
            <th 
              v-for="column in visibleColumns" 
              :key="column.key" 
              :class="['column-header', { 'sortable': column.sortable }]"
              @click="column.sortable && handleSort(column.key)"
            >
              <div class="th-content">
                {{ column.label }}
                <span v-if="column.sortable" class="sort-indicator">
                  <span 
                    class="sort-arrow up" 
                    :class="{ 'active': sortKey === column.key && sortOrder === 'asc' }"
                  >▲</span>
                  <span 
                    class="sort-arrow down" 
                    :class="{ 'active': sortKey === column.key && sortOrder === 'desc' }"
                  >▼</span>
                </span>
              </div>
            </th>
            <th v-if="hasActions" class="actions-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading || paginatedData.length === 0" class="empty-row">
            <td :colspan="visibleColumns.length + (hasActions ? 1 : 0)" class="empty-cell">
              <div v-if="isLoading" class="table-loader">
                <div class="loader-spinner"></div>
                <span>Loading data...</span>
              </div>
              <div v-else class="no-data">
                No data available
              </div>
            </td>
          </tr>
          <tr v-for="(item, rowIndex) in paginatedData" :key="rowIndex" class="data-row">
            <td 
              v-for="column in visibleColumns" 
              :key="`${rowIndex}-${column.key}`" 
              :class="[`cell-${column.key}`, { 'numeric': column.numeric }]"
            >
              <div v-if="column.template" v-html="column.template(item[column.key], item)"></div>
              <template v-else>
                <span v-if="column.prefix">{{ column.prefix }}</span>
                {{ formatCellValue(item[column.key], column) }}
                <span v-if="column.suffix">{{ column.suffix }}</span>
              </template>
            </td>
            <td v-if="hasActions" class="actions-cell">
              <slot name="row-actions" :item="item" :index="rowIndex"></slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Pagination -->
    <div v-if="showPagination" class="pagination">
      <div class="pagination-info">
        Showing {{ paginationInfo.start }} to {{ paginationInfo.end }} of {{ filteredData.length }} entries
      </div>
      <div class="pagination-controls">
        <button 
          class="pagination-button" 
          :disabled="currentPage === 1"
          @click="currentPage = 1"
        >
          &laquo;
        </button>
        <button 
          class="pagination-button" 
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          &lt;
        </button>
        
        <div class="page-numbers">
          <button 
            v-for="page in displayedPages" 
            :key="page" 
            class="page-number" 
            :class="{ 'active': currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>
        </div>
        
        <button 
          class="pagination-button" 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          &gt;
        </button>
        <button 
          class="pagination-button" 
          :disabled="currentPage === totalPages"
          @click="currentPage = totalPages"
        >
          &raquo;
        </button>
      </div>
      <div class="pagination-size">
        <select v-model="pageSize" class="page-size-select">
          <option :value="10">10 / page</option>
          <option :value="25">25 / page</option>
          <option :value="50">50 / page</option>
          <option :value="100">100 / page</option>
        </select>
      </div>
    </div>
    
    <div v-if="isLoading" class="loading-shimmer"></div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';

export default {
  name: 'MetricsTable',
  props: {
    title: {
      type: String,
      default: ''
    },
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true,
      // Each column should have: { key, label, sortable (optional), 
      // numeric (optional), prefix (optional), suffix (optional), 
      // formatter (optional), template (optional function) }
    },
    filters: {
      type: Array,
      default: () => [],
      // Each filter should have: { key, label, options: [{ value, label }] }
    },
    defaultSort: {
      type: Object,
      default: () => ({ key: '', order: 'asc' })
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Boolean,
      default: true
    },
    defaultPageSize: {
      type: Number,
      default: 10
    }
  },
  setup(props, { slots }) {
    // Search and filter
    const searchQuery = ref('');
    const activeFilters = ref({});
    
    // Sorting
    const sortKey = ref(props.defaultSort.key || '');
    const sortOrder = ref(props.defaultSort.order || 'asc');
    
    // Pagination
    const currentPage = ref(1);
    const pageSize = ref(props.defaultPageSize);
    
    // Initialize active filters
    watch(() => props.filters, (newFilters) => {
      newFilters.forEach(filter => {
        if (!activeFilters.value[filter.key]) {
          activeFilters.value[filter.key] = '';
        }
      });
    }, { immediate: true });
    
    // Reset to first page when filters change
    watch([searchQuery, activeFilters, pageSize], () => {
      currentPage.value = 1;
    });
    
    // Check if the table has action slot
    const hasActions = computed(() => {
      return !!slots['row-actions'];
    });
    
    // Only show columns with visible=true or undefined
    const visibleColumns = computed(() => {
      return props.columns.filter(col => col.visible !== false);
    });
    
    // Filter data based on search and active filters
    const filteredData = computed(() => {
      let result = [...props.data];
      
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(item => {
          return Object.keys(item).some(key => {
            const column = props.columns.find(col => col.key === key);
            if (!column || column.searchable === false) return false;
            
            const value = item[key];
            if (value === null || value === undefined) return false;
            
            return String(value).toLowerCase().includes(query);
          });
        });
      }
      
      // Apply active filters
      Object.entries(activeFilters.value).forEach(([key, value]) => {
        if (value) {
          result = result.filter(item => {
            // Handle array values
            if (Array.isArray(item[key])) {
              return item[key].includes(value);
            }
            return item[key] === value;
          });
        }
      });
      
      // Apply sorting
      if (sortKey.value) {
        result.sort((a, b) => {
          const aValue = a[sortKey.value];
          const bValue = b[sortKey.value];
          
          // Handle null/undefined values
          if (aValue === null || aValue === undefined) return sortOrder.value === 'asc' ? -1 : 1;
          if (bValue === null || bValue === undefined) return sortOrder.value === 'asc' ? 1 : -1;
          
          // Handle numeric sorting
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortOrder.value === 'asc' ? aValue - bValue : bValue - aValue;
          }
          
          // String comparison
          const aString = String(aValue).toLowerCase();
          const bString = String(bValue).toLowerCase();
          
          return sortOrder.value === 'asc' 
            ? aString.localeCompare(bString) 
            : bString.localeCompare(aString);
        });
      }
      
      return result;
    });
    
    // Paginated data
    const paginatedData = computed(() => {
      if (!props.pagination) return filteredData.value;
      
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      
      return filteredData.value.slice(start, end);
    });
    
    // Total pages
    const totalPages = computed(() => {
      return Math.max(1, Math.ceil(filteredData.value.length / pageSize.value));
    });
    
    // Displayed page numbers
    const displayedPages = computed(() => {
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;
      
      range.push(1);
      
      for (let i = currentPage.value - delta; i <= currentPage.value + delta; i++) {
        if (i < totalPages.value && i > 1) {
          range.push(i);
        }
      }
      
      range.push(totalPages.value);
      
      for (let i of range) {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      }
      
      return rangeWithDots;
    });
    
    // Pagination info
    const paginationInfo = computed(() => {
      const start = filteredData.value.length === 0 
        ? 0 
        : (currentPage.value - 1) * pageSize.value + 1;
        
      const end = Math.min(start + pageSize.value - 1, filteredData.value.length);
      
      return { start, end };
    });
    
    // Format cell value
    const formatCellValue = (value, column) => {
      if (value === null || value === undefined) return '';
      
      // Use column formatter if provided
      if (column.formatter) {
        return column.formatter(value);
      }
      
      // Format based on value type
      if (typeof value === 'number') {
        // Format as currency if has currency prefix
        if (column.prefix === '$') {
          return value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        
        // Format percentages
        if (column.suffix === '%') {
          return value.toFixed(1);
        }
        
        // Regular number formatting
        return value.toLocaleString();
      }
      
      // Format dates
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      
      // Default string formatting
      return value;
    };
    
    // Handle sorting
    const handleSort = (key) => {
      if (sortKey.value === key) {
        // Toggle order if same key
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      } else {
        // New sort key
        sortKey.value = key;
        sortOrder.value = 'asc';
      }
    };
    
    // Show pagination controls
    const showPagination = computed(() => {
      return props.pagination && filteredData.value.length > 0;
    });
    
    return {
      searchQuery,
      activeFilters,
      sortKey,
      sortOrder,
      currentPage,
      pageSize,
      hasActions,
      visibleColumns,
      filteredData,
      paginatedData,
      totalPages,
      displayedPages,
      paginationInfo,
      formatCellValue,
      handleSort,
      showPagination
    };
  }
};
</script>

<style scoped>
.metrics-table {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.table-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.table-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
}

.table-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.search-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 220px;
  color: #1e293b;
  background-color: #f8fafc;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #93c5fd;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filters-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.filter {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-label {
  font-size: 0.875rem;
  color: #64748b;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  background-color: #f8fafc;
  transition: all 0.2s ease;
}

.filter-select:focus {
  outline: none;
  border-color: #93c5fd;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.table-responsive {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.column-header {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
}

.column-header.sortable {
  cursor: pointer;
  user-select: none;
}

.column-header.sortable:hover {
  background-color: #f1f5f9;
}

.th-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sort-indicator {
  display: flex;
  flex-direction: column;
  margin-left: 0.5rem;
  line-height: 0.5;
}

.sort-arrow {
  font-size: 0.6rem;
  color: #cbd5e1;
  transition: color 0.2s ease;
}

.sort-arrow.active {
  color: #3b82f6;
}

.data-row {
  transition: background-color 0.2s ease;
}

.data-row:hover {
  background-color: #f8fafc;
}

.data-row td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #334155;
}

.numeric {
  text-align: right;
}

.actions-cell {
  text-align: right;
  white-space: nowrap;
}

.empty-row {
  height: 200px;
}

.empty-cell {
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
}

.table-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.loader-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Pagination */
.pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  gap: 1rem;
}

.pagination-info {
  font-size: 0.875rem;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-button:hover:not(:disabled) {
  background-color: #e2e8f0;
  color: #334155;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.page-number {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-number:hover:not(.active) {
  background-color: #e2e8f0;
  color: #334155;
}

.page-number.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.page-size-select {
  padding: 0.4rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #64748b;
  background-color: #f8fafc;
  transition: all 0.2s ease;
}

.page-size-select:focus {
  outline: none;
  border-color: #93c5fd;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Loading state */
.metrics-table.loading {
  pointer-events: none;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
  pointer-events: none;
  z-index: 10;
}

.metrics-table.loading::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.4);
  pointer-events: none;
  z-index: 5;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .table-controls {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .filter {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .pagination {
    flex-direction: column;
    align-items: center;
  }
  
  .pagination-info {
    text-align: center;
  }
}
</style> 